import { useEffect, useState } from 'react'
import DOMPurify from 'dompurify'
import { format, parseISO, isValid } from 'date-fns'
import { es } from 'date-fns/locale'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { seAbreSinConfirmar } from '@/lib/casillero'
import { EstadoBadge } from './EstadoBadge'
import {
  AlertTriangle,
  Building2,
  Calendar,
  Download,
  File,
  FileArchive,
  FileText,
  Image as ImageIcon,
  Loader2,
  Paperclip,
} from 'lucide-react'
import type {
  ComunicacionResumen,
  ComunicacionDetalle as ComunicacionDetalleType,
  AdjuntoComunicacion,
} from '@/types/afip'

interface ComunicacionDetalleProps {
  comunicacion: ComunicacionResumen | null
  detalle: ComunicacionDetalleType | null
  isLoading: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirmarAbrir: (comunicacion: ComunicacionResumen) => void
  /** Si false, oculta los botones que disparan `consumirComunicacion` (perfeccionan la notificación legal). */
  puedeAbrir?: boolean
  /** CUIT del contribuyente, mostrado en la confirmación legal para evitar errores de cliente equivocado. */
  cuitContribuyente?: string | null
}

function formatearFechaLarga(value: string): string {
  if (!value) return '—'
  const isoLike = value.includes('T') ? value : value.replace(' ', 'T')
  const d = parseISO(isoLike)
  return isValid(d) ? format(d, "dd 'de' MMMM yyyy 'a las' HH:mm", { locale: es }) : value
}

function IconoAdjunto({ tipo }: { tipo: string }) {
  const t = tipo.toLowerCase()
  if (t.includes('pdf')) return <FileText className="h-5 w-5 text-red-500" />
  if (t.includes('image')) return <ImageIcon className="h-5 w-5 text-blue-500" />
  if (t.includes('zip') || t.includes('compressed'))
    return <FileArchive className="h-5 w-5 text-amber-600" />
  return <File className="h-5 w-5 text-muted-foreground" />
}

function AdjuntoCard({ adjunto }: { adjunto: AdjuntoComunicacion }) {
  return (
    <div className="flex items-center gap-3 rounded-md border p-3 bg-card">
      <IconoAdjunto tipo={adjunto.tipo} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{adjunto.nombre}</p>
        <p className="text-xs text-muted-foreground">{adjunto.tipo}</p>
      </div>
      <Button
        size="sm"
        variant="ghost"
        disabled
        title="La descarga de adjuntos estará disponible en una versión futura"
        aria-label={`Descargar ${adjunto.nombre} (próximamente)`}
      >
        <Download className="h-4 w-4" />
      </Button>
    </div>
  )
}

export function ComunicacionDetalleDialog({
  comunicacion,
  detalle,
  isLoading,
  open,
  onOpenChange,
  onConfirmarAbrir,
  puedeAbrir = true,
  cuitContribuyente = null,
}: ComunicacionDetalleProps) {
  const [showConfirmacion, setShowConfirmacion] = useState(false)
  const [autorizacionConfirmada, setAutorizacionConfirmada] = useState(false)

  // Resetear el checkbox al cerrar el AlertDialog (no carry-over entre comunicaciones).
  useEffect(() => {
    if (!showConfirmacion) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAutorizacionConfirmada(false)
    }
  }, [showConfirmacion])

  if (!comunicacion) return null

  // Una sola pregunta gobierna el aviso legal y el botón: si abrir esta comunicación todavía
  // perfecciona la notificación. El estado que ARCA no dejó clasificar cuenta como no leída.
  const pideConfirmacion = !seAbreSinConfirmar(comunicacion.estado)
  const adjuntos = detalle?.adjuntos ?? []

  const handleVerContenido = () => {
    if (pideConfirmacion) {
      setShowConfirmacion(true)
    } else if (!detalle) {
      onConfirmarAbrir(comunicacion)
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader className="space-y-3">
            <div className="flex items-start justify-between gap-3">
              <DialogTitle className="text-lg leading-tight pr-2">
                {comunicacion.asunto}
              </DialogTitle>
              <div className="shrink-0 mt-0.5">
                <EstadoBadge
                  estado={comunicacion.estado}
                  raw={comunicacion.estadoRaw}
                />
              </div>
            </div>
            <DialogDescription className="sr-only">
              Detalle de la comunicación del Domicilio Fiscal Electrónico de ARCA
            </DialogDescription>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                {formatearFechaLarga(comunicacion.fechaEmision)}
              </span>
              <span className="flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5" />
                {comunicacion.sistemaPublicador}
              </span>
              {comunicacion.tieneAdjunto && (
                <span className="flex items-center gap-1.5">
                  <Paperclip className="h-3.5 w-3.5" />
                  {adjuntos.length > 0
                    ? `${adjuntos.length} adjunto${adjuntos.length !== 1 ? 's' : ''}`
                    : 'Con adjunto'}
                </span>
              )}
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto py-4">
            {detalle ? (
              <div className="space-y-4">
                <div className="rounded-md border bg-muted/30 p-4">
                  <div
                    className="prose prose-sm dark:prose-invert max-w-none [&_p]:my-2 [&_table]:text-xs"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(detalle.cuerpo),
                    }}
                  />
                </div>

                {adjuntos.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">
                      Adjuntos ({adjuntos.length})
                    </p>
                    <div className="grid gap-2">
                      {adjuntos.map((adj, i) => (
                        <AdjuntoCard key={i} adjunto={adj} />
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground italic">
                      La descarga de adjuntos estará disponible en una versión
                      futura.
                    </p>
                  </div>
                )}
              </div>
            ) : pideConfirmacion ? (
              <div className="text-center py-8 space-y-4">
                <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto" />
                <div>
                  <p className="font-medium">Esta comunicación no figura como leída</p>
                  <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                    Abrir esta comunicación la da por notificada bajo la{' '}
                    <strong>RG (AFIP) 4280/2018</strong>, y eso no se puede
                    deshacer. Solo abrila si tenés autorización expresa del
                    cliente.
                  </p>
                </div>
                {puedeAbrir ? (
                  <Button onClick={handleVerContenido} disabled={isLoading}>
                    {isLoading && (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    )}
                    Ver contenido
                  </Button>
                ) : (
                  <p className="text-xs text-destructive">
                    Tu rol no tiene permiso para abrir comunicaciones del
                    Casillero. Pedile al contador principal que la abra.
                  </p>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <Button onClick={handleVerContenido} disabled={isLoading}>
                  {isLoading && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  Cargar contenido
                </Button>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmación legal para comunicaciones no leídas — RG 4280/2018 */}
      <AlertDialog
        open={showConfirmacion}
        onOpenChange={setShowConfirmacion}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Notificación fehaciente — RG (AFIP) 4280/2018
            </AlertDialogTitle>
          </AlertDialogHeader>

          <div className="space-y-3 text-sm">
            <p>
              Abrir esta comunicación del Domicilio Fiscal Electrónico
              <strong> notifica fehacientemente</strong> al contribuyente, y
              eso no se puede deshacer. Revisá los plazos para responder con
              el cliente.
            </p>

            <div className="rounded-md bg-muted/60 p-3 text-xs space-y-1">
              <p>
                <span className="text-muted-foreground">Comunicación: </span>
                <span className="font-medium">{comunicacion.asunto}</span>
              </p>
              {cuitContribuyente && (
                <p>
                  <span className="text-muted-foreground">CUIT contribuyente: </span>
                  <span className="font-mono">{cuitContribuyente}</span>
                </p>
              )}
              <p>
                <span className="text-muted-foreground">Emisor: </span>
                {comunicacion.sistemaPublicador}
              </p>
            </div>

            <label className="flex items-start gap-2 text-xs cursor-pointer">
              <Checkbox
                checked={autorizacionConfirmada}
                onCheckedChange={(c) => setAutorizacionConfirmada(c === true)}
                className="mt-0.5"
              />
              <span>
                Tengo autorización expresa del cliente para abrir esta
                notificación. Esta acción no se puede revertir desde el sistema.
              </span>
            </label>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={!autorizacionConfirmada}
              onClick={() => {
                setShowConfirmacion(false)
                onConfirmarAbrir(comunicacion)
              }}
            >
              Sí, abrir comunicación
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
